import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Alert,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { AuthContext } from '../../authContext/context';
import { postRequest } from '../../lib/api';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useForm, Controller } from 'react-hook-form';

// Customer Search Modal Component
function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd, selectedCustomers, title }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounceRef = React.useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
      console.log('sfjjjafs')
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isCustomerAdded = (customerId) => {
    return selectedCustomers.some(c => c._id === customerId);
  };

  const handleCreateCustomer = async () => {
    // Check if at least email or phone is provided
    if (!searchFields.email && !searchFields.phone_number) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please provide at least an email or phone number to create a customer.'
      });
      return;
    }

    try {
      setCreating(true);
      const payload = {
        title: searchFields.title || '',
        forename: searchFields.forename || '',
        surname: searchFields.surname || '',
        email: searchFields.email || '',
        phone_number: searchFields.phone_number || '',
        dob: '',
        address1: '',
        address2: '',
        town: '',
        country: '',
        postcode: ''
      };

      const res = await postRequest('private/createCustomer', payload, authToken);

      if (res?.status === 200 && res?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Customer created successfully!',
          timer: 1500,
          showConfirmButton: false
        });

        // Add the newly created customer
        onCustomerAdd(res.data);

        // Reset search fields
        setSearchFields({
          title: '',
          forename: '',
          surname: '',
          email: '',
          phone_number: ''
        });

        // Close modal
        onHide();
      } else {
        throw new Error(res?.message || 'Failed to create customer');
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to create customer'
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isCustomerAdded(customer._id)}
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          {isCustomerAdded(customer._id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className='text-center'>
                      <Box sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                          No customers found
                        </Typography>
                        {(searchFields.email || searchFields.phone_number || searchFields.forename || searchFields.surname) && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateCustomer}
                            disabled={creating}
                            sx={{
                              backgroundColor: '#6f42c1',
                              '&:hover': {
                                backgroundColor: '#5a32a3'
                              }
                            }}
                          >
                            {creating ? 'Creating...' : 'Add Customer'}
                          </Button>
                        )}
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default function TransferOfEquityDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Support both 'customerId' and 'customer_id' parameter names
  const customerIdFromUrl = searchParams.get('customerId') || searchParams.get('customer_id');
  const mode = searchParams.get('mode'); // 'revise' mode for revising quotes
  const authCtx = useContext(AuthContext);

  // Get basicFormData from previous step (SimpleConveyancingForm)
  const basicFormData = location.state?.basicFormData || {};
  console.log('📋 Basic Form Data from Step 1 (Transfer of Equity):', basicFormData);
  
  const [conveyancingData, setConveyancingData] = useState(null);
  const [customersToAdd, setCustomersToAdd] = useState([]);
  const [customersToRemove, setCustomersToRemove] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [partners, setPartners] = useState([]);
  const [calculationData, setCalculationData] = useState(null);

  // Postcode validation states
  const [postcodeValue, setPostcodeValue] = useState('');
  const [checkingPostcode, setCheckingPostcode] = useState(false);
  const [postcodeMatch, setPostcodeMatch] = useState(null);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isPostcodeValid, setIsPostcodeValid] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    getValues
  } = useForm({
    defaultValues: {
      // Property Details
      line1: '',
      line2: '',
      town: '',
      county: '',
      postcode: '',
      propertyValue: '',
      transferType: 'whole',
      considerationValue: '',
      ownershipType: '',
      lender_panel_id: '',
      partner_id: '',

      // Info section
      isMortgaged: false,
      requiresLocalSearchInsurance: false,
      firstTimeBuyer: false
    }
  });

  // Transfer type options
  const transferTypes = [
    { value: 'whole', label: 'Whole' },
    { value: 'part', label: 'Part' }
  ];

  // Ownership type options
  const ownershipTypes = [
    { value: 'freehold', label: 'Freehold' },
    { value: 'leasehold', label: 'Leasehold' }
  ];

  // Fetch active lender panels
  const fetchActiveLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
      }
    } catch (error) {
      console.error('Error fetching lender panels:', error);
    }
  };

  // Fetch partners
  const fetchPartners = async () => {
    try {
      const response = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (response?.status === 200 && response?.data?.docs) {
        setPartners(response.data.docs);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // 2-step postcode validation with branch matching
  useEffect(() => {
    if (!postcodeValue || postcodeValue.trim().length === 0) {
      setIsPostcodeValid(false);
      return;
    }

    const checkPostcode = async () => {
      setCheckingPostcode(true);
      try {
        setPostcodeMatch(null);
        setAvailableBranches([]);
        setSelectedBranch(null);
        setIsPostcodeValid(false);

        // STEP 1: Check if postcode exists
        const checkRes = await postRequest(
          'private/checkPostcodeInBranch',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        if (checkRes?.status === 406 || checkRes?.data?.exists === false) {
          setIsPostcodeValid(false);
          Swal.fire({
            title: 'Invalid Postcode',
            text: checkRes?.message || 'This postcode does not exist in the UK postcode database.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
          return;
        }

        // STEP 2: Match branch by postcode
        const matchRes = await postRequest(
          'private/matchBranchByPostcode',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        if (matchRes?.data?.matched) {
          setPostcodeMatch(matchRes.data.branch);
          setIsPostcodeValid(true);
        } else {
          setIsPostcodeValid(false);
          Swal.fire({
            title: 'No Branch Coverage',
            text: 'This postcode area is not currently served by any of our branches.',
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
        }
      } catch (error) {
        setIsPostcodeValid(false);
      } finally {
        setCheckingPostcode(false);
      }
    };

    const timeoutId = setTimeout(checkPostcode, 500);
    return () => clearTimeout(timeoutId);
  }, [postcodeValue, authCtx.token]);

  // Fetch calculation data for revise quote mode
  const fetchCalculationData = async () => {
    try {
      console.log('📊 Fetching calculation data for revise quote mode...');

      // Get partner_id from current form values if exists
      const currentFormValues = getValues();
      const partnerId = currentFormValues.partner_id || null;
      console.log('🤝 Partner ID for fetch:', partnerId);

      const payload = partnerId ? { partner_id: partnerId } : {};
      const response = await postRequest(`private/fetch_lead_calculations/${id}`, payload, authCtx.token);

      if (response?.status === 200 && response?.data) {
        console.log('✅ Calculation data fetched:', response.data);

        // Store the calculation data to display in the form
        setCalculationData(response.data);

        // Show success message
        Swal.fire({
          icon: 'success',
          title: 'Quote Loaded',
          text: 'Quote calculation has been loaded for revision.',
          timer: 2000,
          showConfirmButton: false
        });

        return response.data;
      } else {
        console.warn('⚠️ No calculation data found');
        return null;
      }
    } catch (error) {
      console.error('❌ Error fetching calculation data:', error);
      Swal.fire({
        icon: 'warning',
        title: 'No Quote Found',
        text: 'No saved quote calculation found for this lead.'
      });
      return null;
    }
  };

  // Fetch conveyancing details
  const fetchConveyancingDetails = async () => {
    try {
      setLoading(true);
      const res = await postRequest(`private/getConveyancingDetails/${id}`, {}, authCtx.token);
      if (res?.status === 200) {
        console.log('📥 Received conveyancing data:', res.data);
        setConveyancingData(res.data);

        // Handle both nested (propertyDetails) and flat structure
        const propDetails = res.data.propertyDetails;
        const flatData = res.data;

        // Set address fields - check both nested and flat structure
        const address = propDetails?.address || flatData.property_address || flatData.address;
        if (address) {
          setValue('line1', address.line1 || address.address1 || '');
          setValue('line2', address.line2 || address.address2 || '');
          setValue('town', address.town || '');
          setValue('county', address.county || address.country || '');
          const prefilledPostcode = address.postcode || '';
          setValue('postcode', prefilledPostcode);
          setPostcodeValue(prefilledPostcode); // trigger postcode validation for prefilled value
        }

        // Set property value fields - check both nested and flat
        const propertyValue = propDetails?.propertyValue || flatData.property_value;
        const transferType = propDetails?.transferType || flatData.transfer_type;
        const considerationValue = propDetails?.considerationValue || flatData.consideration_value;
        const ownershipType = propDetails?.ownershipType || flatData.ownership_type;

        if (propertyValue) setValue('propertyValue', propertyValue);
        if (transferType) setValue('transferType', transferType);
        if (considerationValue) setValue('considerationValue', considerationValue);
        if (ownershipType) setValue('ownershipType', ownershipType);

        // Set lender panel
        if (res.data.lender_panel_id) {
          const lenderPanelId = typeof res.data.lender_panel_id === 'object'
            ? res.data.lender_panel_id._id
            : res.data.lender_panel_id;
          console.log('🏦 Setting Lender Panel ID:', lenderPanelId);
          setValue('lender_panel_id', lenderPanelId);
        }

        // Partner
        const partnerId = res.data.partner_id
          ? (typeof res.data.partner_id === 'object' ? res.data.partner_id._id : res.data.partner_id)
          : '';
        console.log('🤝 Partner ID:', partnerId);
        if (partnerId) {
          setValue('partner_id', String(partnerId));
        }

        // Set info fields - check both info and conveyancing_details
        const infoData = res.data.info || res.data.conveyancing_details || {};
        console.log('ℹ️ Setting info fields:', infoData);

        const isMortgaged = infoData.isMortgaged ?? infoData.is_mortgaged;
        const requiresLocalSearchInsurance = infoData.requiresLocalSearchInsurance ?? infoData.requires_local_search_insurance;
        const firstTimeBuyer = infoData.firstTimeBuyer ?? infoData.is_first_time_buyer;

        if (isMortgaged !== undefined) setValue('isMortgaged', isMortgaged);
        if (requiresLocalSearchInsurance !== undefined) setValue('requiresLocalSearchInsurance', requiresLocalSearchInsurance);
        if (firstTimeBuyer !== undefined) setValue('firstTimeBuyer', firstTimeBuyer);

        // Set customers from lead data - check multiple field names
        const customersToAddData = res.data.customersToAdd || res.data.customers_to_add || res.data.customers || [];
        const customersToRemoveData = res.data.customersToRemove || res.data.customers_to_remove || res.data.customers || [];

        console.log('👥 Raw customer data from API:', {
          customersToAdd: res.data.customersToAdd,
          customers_to_add: res.data.customers_to_add,
          customersToRemove: res.data.customersToRemove,
          customers_to_remove: res.data.customers_to_remove,
          customers: res.data.customers
        });

        // Set Customers to Add
        if (Array.isArray(customersToAddData) && customersToAddData.length > 0) {
          // Filter out any non-populated customers (just IDs)
          const populatedCustomers = customersToAddData.filter(c =>
            c && typeof c === 'object' && (c.forename || c.surname || c.email)
          );
          if (populatedCustomers.length > 0) {
            console.log('👥 Setting customers to add:', populatedCustomers.length);
            setCustomersToAdd(populatedCustomers);
          } else {
            console.log('⚠️ Customers to add are not populated, skipping');
          }
        } else {
          console.log('⚠️ No customers to add found');
        }

        // Set Customers to Remove
        if (Array.isArray(customersToRemoveData) && customersToRemoveData.length > 0) {
          // Filter out any non-populated customers (just IDs)
          const populatedCustomers = customersToRemoveData.filter(c =>
            c && typeof c === 'object' && (c.forename || c.surname || c.email)
          );
          if (populatedCustomers.length > 0) {
            console.log('👥 Setting customers to remove:', populatedCustomers.length);
            setCustomersToRemove(populatedCustomers);
          } else {
            console.log('⚠️ Customers to remove are not populated, skipping');
          }
        } else {
          console.log('⚠️ No customers to remove found');
        }
      }
    } catch (error) {
      console.error('Error fetching conveyancing details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && id !== 'new' && authCtx.token) {
      fetchConveyancingDetails(); // ✅ Uncommented for autofill to work

      // If in revise mode, also fetch calculation data
      if (mode === 'revise') {
        fetchCalculationData();
      }
    }
  }, [id, authCtx.token, mode]);

  // Fetch active lender panels on mount
  useEffect(() => {
    fetchActiveLenderPanels();
    fetchPartners();
  }, [authCtx.token]);

  // Auto-add customer from URL if provided
  useEffect(() => {
    const fetchCustomerFromUrl = async () => {
      console.log('🎯 useEffect triggered:', {
        customerIdFromUrl,
        hasToken: !!authCtx.token,
        hasConveyancingData: !!conveyancingData,
        customersToAddLength: customersToAdd.length
      });

      // Only run if we have customerIdFromUrl and token, and we're creating a new lead (no conveyancingData yet)
      if (!customerIdFromUrl || !authCtx.token || conveyancingData) {
        console.log('⏭️ Skipping customer fetch - either missing requirements or lead data already loaded');
        return;
      }

      console.log('🔍 Customer ID from URL:', customerIdFromUrl);

      // Check if customer is already added to either list (check both _id and customer_id fields)
      const alreadyInAdd = customersToAdd.some(c => c._id === customerIdFromUrl || c.customer_id === customerIdFromUrl);
      const alreadyInRemove = customersToRemove.some(c => c._id === customerIdFromUrl || c.customer_id === customerIdFromUrl);

      if (alreadyInAdd && alreadyInRemove) {
        console.log('✅ Customer already in both lists');
        return;
      }

      try {
        console.log('📡 Fetching customer details for:', customerIdFromUrl);

        // Use getCustomerIndividualData endpoint (same as used in edit forms)
        const res = await postRequest(`private/getCustomerIndividualData/${customerIdFromUrl}`, {}, authCtx.token);

        console.log('📥 Customer fetch response:', res);

        if (res?.status === 200 && res?.data) {
          console.log('✅ Auto-adding customer from URL:', res.data);

          // Add to Customers to Add ONLY (not to Customers to Remove)
          setCustomersToAdd(prev => {
            // Double-check not already added
            if (prev.some(c => c._id === res.data._id)) {
              console.log('⚠️ Customer already exists in customersToAdd, not adding again');
              return prev;
            }
            console.log('➕ Adding customer to customersToAdd');
            return [...prev, res.data];
          });

          // REMOVED: No longer auto-adding to Customers to Remove from URL
          // User should manually add customers to remove if needed
        } else {
          console.log('❌ Invalid response:', res);
        }
      } catch (error) {
        console.error('❌ Error fetching customer from URL:', error);
      }
    };

    fetchCustomerFromUrl();
  }, [customerIdFromUrl, authCtx.token, conveyancingData, customersToAdd, customersToRemove]);

  const handleAddCustomerToAdd = (customer) => {
    if (!customersToAdd.some(c => c._id === customer._id)) {
      setCustomersToAdd([...customersToAdd, customer]);
    }
  };

  const handleRemoveCustomerToAdd = (customerId) => {
    setCustomersToAdd(customersToAdd.filter(c => c._id !== customerId));
  };

  const handleAddCustomerToRemove = (customer) => {
    if (!customersToRemove.some(c => c._id === customer._id)) {
      setCustomersToRemove([...customersToRemove, customer]);
    }
  };

  const handleRemoveCustomerToRemove = (customerId) => {
    setCustomersToRemove(customersToRemove.filter(c => c._id !== customerId));
  };

  const onSubmit = async (formData) => {
    // Block save until both postcode checks (UK database + branch match) have passed
    if (checkingPostcode) {
      Swal.fire({
        icon: 'info',
        title: 'Please Wait',
        text: 'Postcode verification is still in progress. Please try again in a moment.'
      });
      return;
    }

    if (!isPostcodeValid) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Postcode',
        text: 'Please enter a valid UK postcode that is covered by one of our branches before saving.'
      });
      return;
    }

    try {
      const isEditing = id && id !== 'new';
      let res;

      if (isEditing) {
        // EDITING EXISTING LEAD - Use update API with nested structure
        console.log('📝 Editing existing Transfer of Equity lead:', id);

        const updatePayload = {
          conveyancing_id: id,
          customersToAdd: customersToAdd.map(c => c._id),
          customersToRemove: customersToRemove.map(c => c._id),
          propertyDetails: {
            address: {
              line1: formData.line1,
              line2: formData.line2,
              town: formData.town,
              county: formData.county,
              postcode: formData.postcode
            },
            propertyValue: formData.propertyValue,
            transferType: formData.transferType,
            considerationValue: formData.considerationValue,
            ownershipType: formData.ownershipType
          },
          info: {
            isMortgaged: formData.isMortgaged,
            requiresLocalSearchInsurance: formData.requiresLocalSearchInsurance,
            firstTimeBuyer: formData.firstTimeBuyer
          },
          lender_panel_id: formData.lender_panel_id || null,
          partner_id: formData.partner_id || null
        };

        console.log('📦 Update Payload:', updatePayload);
        res = await postRequest(
          'private/updateConveyancingTransferOfEquity',
          updatePayload,
          authCtx.token
        );
      } else {
        // CREATING NEW LEAD - Use save API with flat structure
        console.log('✨ Creating new Transfer of Equity lead');

        const payload = {
          // Data from Step 1 (SimpleConveyancingForm)
          override_date: basicFormData?.override_date || new Date(),
          group_code: basicFormData?.group_code,
          acquisition: basicFormData?.acquisition || 'Direct',
          product_id: basicFormData?.product_id,
          service_type: basicFormData?.service_type || 'Transfer of Equity',

          customers_to_add: customersToAdd.map(c => c._id),
          customers_to_remove: customersToRemove.map(c => c._id),
          property_address: {
            address1: formData.line1,
            address2: formData.line2,
            town: formData.town,
            country: formData.county,
            postcode: formData.postcode
          },
          property_value: formData.propertyValue,
          transfer_type: formData.transferType,
          consideration_value: formData.considerationValue,
          ownership_type: formData.ownershipType,
          lender_panel_id: formData.lender_panel_id || null,
          partner_id: formData.partner_id || null,
          is_mortgaged: formData.isMortgaged,
          requires_local_search_insurance: formData.requiresLocalSearchInsurance,
          is_first_time_buyer: formData.firstTimeBuyer
        };

        const actualLeadId = 'new';
        res = await postRequest(
          `private/saveConveyancingTransferOfEquityToLead/${actualLeadId}`,
          payload,
          authCtx.token
        );
      }

      if (res?.status === 'success' || res?.status === 200) {
        // Get the actual lead_id from response
        const savedLeadId = res?.data?.lead_id || id;

        // 🎯 Call unified API to calculate and save to final_leads_calculations
        try {
          // ⏱️ Wait 300ms for database to commit flags before calculating fees
          console.log('⏳ Waiting 300ms for database to commit flags...');
          await new Promise(resolve => setTimeout(resolve, 300));

          console.log('💰 Calling calculateAndSaveLeadQuote for Transfer of Equity...');

          const feeResponse = await postRequest('private/calculateAndSaveLeadQuote', {
            lead_id: savedLeadId,
            service_type: 'Transfer_of_Equity',
            product_id: basicFormData?.product_id,
            ownership_type: formData.ownershipType,
            property_value: formData.propertyValue || 0,
            postcode: formData.postcode,
            partner_id: formData.partner_id || null
          }, authCtx.token);

          console.log('✅ calculateAndSaveLeadQuote response:', feeResponse);
        } catch (feeError) {
          console.error('⚠️ Error calling calculateAndSaveLeadQuote:', feeError);
          // Non-blocking - don't fail the main operation
        }

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: isEditing
            ? 'Transfer of Equity details updated successfully!'
            : 'Transfer of Equity created and email sent successfully!'
        }).then(() => {
          navigate(`/leads/view/transfer-of-equity/${savedLeadId}`);
        });
      } else {
        throw new Error(res?.message || 'Failed to save details');
      }
    } catch (error) {
      console.error('Error saving details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to save details'
      });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: 'Edit Conveyancing', path: '' },
        ]}
      />
      <br />

      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
            {mode === 'revise' ? 'Revise Quote' : 'Edit Conveyancing'} #{conveyancingData?.reference || id} - Transfer of Equity
          </Typography>

          {/* Quote Summary - Show when in revise mode and calculation data is available */}
          {mode === 'revise' && calculationData && (
            <Box sx={{ mb: 4, p: 3, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#495057' }}>
                Current Quote Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Net Amount</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                    £{(calculationData?.totals?.net || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">VAT</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                    £{(calculationData?.totals?.vat || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Gross Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#28a745' }}>
                    £{(calculationData?.totals?.gross || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 2 }}>
                You are revising the quote. Update the fields below and save to recalculate.
              </Alert>
            </Box>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Transfer of Equity Details Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                Transfer of Equity Details
              </Typography>
              
              {/* Property Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Property
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Address</Typography>
                  <Controller
                    name="line1"
                    control={control}
                    rules={{ required: 'Address Line 1 is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 1"
                        variant="outlined"
                        size="small"
                        error={!!errors.line1}
                        helperText={errors.line1?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="line2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 2"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="town"
                    control={control}
                    rules={{ required: 'Town is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Town"
                        variant="outlined"
                        size="small"
                        error={!!errors.town}
                        helperText={errors.town?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="county"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="County"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="postcode"
                    control={control}
                    rules={{ required: 'Postcode is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Postcode"
                        variant="outlined"
                        size="small"
                        error={!!errors.postcode}
                        helperText={errors.postcode?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          setPostcodeValue(e.target.value);
                        }}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />

                  {/* Checking postcode status */}
                  {checkingPostcode && postcodeValue && (
                    <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      Checking postcode in branches...
                    </Alert>
                  )}

                  {/* Postcode match found */}
                  {postcodeMatch && !checkingPostcode && (
                    <Alert severity="success" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      ✓ Postcode found in branch: <strong>{postcodeMatch.name}</strong>
                    </Alert>
                  )}

                  {/* Branch selection dropdown */}
                  {!postcodeMatch && availableBranches.length > 0 && !checkingPostcode && (
                    <Box sx={{ mt: 1 }}>
                      <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        Postcode not found in any branch. Please select a branch:
                      </Alert>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Branch</InputLabel>
                        <Select
                          value={selectedBranch || ''}
                          onChange={(e) => {
                            setSelectedBranch(e.target.value);
                            setIsPostcodeValid(true);
                          }}
                          label="Select Branch"
                        >
                          {availableBranches.map((branch) => (
                            <MenuItem key={branch._id} value={branch._id}>
                              {branch.name} - {branch.address?.postcode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Property Value</Typography>
                  <Controller
                    name="propertyValue"
                    control={control}
                    rules={{ required: 'Property value is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        variant="outlined"
                        size="small"
                        type="number"
                        slotProps={{
                          input: {
                            startAdornment: <InputAdornment position="start">£</InputAdornment>,
                          }
                        }}
                        error={!!errors.propertyValue}
                        helperText={errors.propertyValue?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Transfer - Whole or Part?</Typography>
                  <Controller
                    name="transferType"
                    control={control}
                    rules={{ required: 'Transfer type is required' }}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" error={!!errors.transferType}>
                        <Select {...field}>
                          {transferTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.transferType && (
                          <Typography variant="caption" color="error">
                            {errors.transferType.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Consideration Value</Typography>
                  <Controller
                    name="considerationValue"
                    control={control}
                    rules={{ required: 'Consideration value is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        variant="outlined"
                        size="small"
                        type="number"
                        slotProps={{
                          input: {
                            startAdornment: <InputAdornment position="start">£</InputAdornment>,
                          }
                        }}
                        error={!!errors.considerationValue}
                        helperText={errors.considerationValue?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Customers Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Customers to Add
              </Typography>

              <Box sx={{ mb: 4 }}>
                {customersToAdd.map((customer) => (
                  <Box
                    key={customer._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 2,
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {customer.title} {customer.forename} {customer.surname}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📧 {customer.email}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📞 {customer.phone_number}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => handleRemoveCustomerToAdd(customer._id)}
                      sx={{
                        minWidth: 'auto',
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))}
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddModal(true)}
                  sx={{ color: '#6f42c1', mt: 1 }}
                >
                  Add Customer
                </Button>
              </Box>

              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Customers to Remove
              </Typography>

              <Box sx={{ mb: 3 }}>
                {customersToRemove.map((customer) => (
                  <Box
                    key={customer._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 2,
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {customer.title} {customer.forename} {customer.surname}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📧 {customer.email}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📞 {customer.phone_number}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => handleRemoveCustomerToRemove(customer._id)}
                      sx={{
                        minWidth: 'auto',
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))}
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => setShowRemoveModal(true)}
                  sx={{ color: '#6f42c1', mt: 1 }}
                >
                  Add Customer
                </Button>
              </Box>
            </Box>

            {/* Lender Panel Section */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Lender Panel
                </Typography>
                <Controller
                  name="lender_panel_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select Lender Panel (Optional)</em>
                        </MenuItem>
                        {lenderPanels.map((panel) => (
                          <MenuItem key={panel._id} value={panel._id}>
                            {panel.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            {/* Partner Section */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Partner
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="partner_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select Partner (Optional)</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={partner._id}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            {/* Ownership Type Section */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Ownership Type
                </Typography>
                <Controller
                  name="ownershipType"
                  control={control}
                  rules={{ required: 'Ownership type is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.ownershipType}>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select Ownership Type</em>
                        </MenuItem>
                        {ownershipTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.ownershipType && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.ownershipType.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            {/* Info Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Info
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {[
                  { name: 'isMortgaged', label: 'Is Mortgaged' },
                  { name: 'requiresLocalSearchInsurance', label: 'Requires Local Search Insurance' },
                  { name: 'firstTimeBuyer', label: 'Is First Time Buyer' }
                ].map((item) => (
                  <Controller
                    key={item.name}
                    name={item.name}
                    control={control}
                    render={({ field }) => (
                      <Button
                        variant="outlined"
                        onClick={() => field.onChange(!field.value)}
                        sx={{
                          borderColor: field.value ? '#6f42c1' : '#ced4da',
                          backgroundColor: field.value ? '#6f42c1' : 'white',
                          color: field.value ? 'white' : '#495057',
                          textTransform: 'none',
                          fontWeight: 400,
                          fontSize: '0.875rem',
                          px: 2,
                          py: 0.75,
                          '&:hover': {
                            backgroundColor: field.value ? '#5a32a3' : '#f8f9fa',
                            borderColor: field.value ? '#5a32a3' : '#adb5bd'
                          }
                        }}
                      >
                        {item.label}
                      </Button>
                    )}
                  />
                ))}
              </Box>
            </Box>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={checkingPostcode || !isPostcodeValid}
                sx={{
                  backgroundColor: '#6f42c1',
                  '&:hover': {
                    backgroundColor: '#5a32a3'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc',
                    color: '#666666'
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      {/* Add Customer Modal */}
      <CustomerSearchModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleAddCustomerToAdd}
        selectedCustomers={customersToAdd}
        title="Add Customer"
      />

      {/* Remove Customer Modal */}
      <CustomerSearchModal
        show={showRemoveModal}
        onHide={() => setShowRemoveModal(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleAddCustomerToRemove}
        selectedCustomers={customersToRemove}
        title="Add Customer to Remove"
      />
    </>
  );
}